import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { BehaviorSubject, firstValueFrom, Observable, throwError } from 'rxjs';
import { catchError, map, timeout } from 'rxjs/operators';
import {
  BuyerVerificationData,
  BuyerVerificationResponse,
  BusinessVerificationFlags,
  GstinItem,
  VerifyPanPayload,
  VerifyShopLicensePayload,
  VerifyUamPayload,
  VerifyUdyamPayload,
} from '../models/buyer-verification.models';
import { applicationConfiguration, AppConfig } from '../interceptor/app.config';
import { canPlaceOrder as canPlaceOrderUtil } from '../utils/gst-eligibility';

const SKIPPED_KEY = 'buyerVerificationSkipped';

export interface OrderCreationGateResult {
  allowed: boolean;
  message: string;
}

@Injectable({
  providedIn: 'root',
})
export class BuyerVerificationService {
  static readonly ORDER_PROFILE_UPDATE_MESSAGE =
    'Please update your profile to complete business verification before placing an order.';

  private readonly endpoint: string;
  private readonly showModalSubject = new BehaviorSubject<boolean>(false);
  private pendingPaymentCallback: (() => void) | null = null;
  private pendingCancelCallback: (() => void) | null = null;

  showModal$ = this.showModalSubject.asObservable();

  constructor(
    private http: HttpClient,
    @Inject(applicationConfiguration) private appConfig: AppConfig,
  ) {
    this.endpoint = `${this.appConfig.apiUrl}api/eol/v1/pv/account/buyer_verification/`;
  }

  getStatus(): Observable<BuyerVerificationData> {
    return this.post({ action: 'get' });
  }

  verifyPan(payload: VerifyPanPayload): Observable<BuyerVerificationData> {
    return this.post({
      action: 'verify_pan',
      pan_number: payload.pan_number,
      full_name: payload.full_name,
      dob: payload.dob,
    });
  }

  fetchGstins(): Observable<BuyerVerificationData> {
    return this.post({ action: 'verify_pan_gst' });
  }

  selectGstin(gstinNumber: string): Observable<BuyerVerificationData> {
    return this.post({
      action: 'verify_pan_gst',
      gstin_number: gstinNumber,
    });
  }

  /** Reads Udyam documents from the current buyer verification profile. */
  fetchUdyam(): Observable<BuyerVerificationData> {
    return this.getStatus();
  }

  verifyUdyam(payload: VerifyUdyamPayload): Observable<BuyerVerificationData> {
    return this.post({
      action: 'verify_udyam',
      udyam_number: payload.udyam_number,
    });
  }

  // Still used by the login flow.
  verifyUam(payload: VerifyUamPayload): Observable<BuyerVerificationData> {
    return this.post({
      action: 'verify_udyam',
      uam_number: payload.uam_number,
    });
  }

  verifyShopLicense(
    payload: VerifyShopLicensePayload,
  ): Observable<BuyerVerificationData> {
    return this.post({
      action: 'verify_shop_license',
      certificate_number: payload.certificate_number,
      state: payload.state,
      name: payload.name,
      commencement_date: payload.commencement_date,
      district: payload.district,
    });
  }

  isGstVerified(data: BusinessVerificationFlags | null | undefined): boolean {
    return this.isTruthyFlag(data?.is_gst_verified);
  }

  isUdyamVerified(data: BusinessVerificationFlags | null | undefined): boolean {
    return this.isTruthyFlag(data?.is_uam_verified);
  }

  isShopLicenseVerified(
    data: BusinessVerificationFlags | null | undefined,
  ): boolean {
    return this.isTruthyFlag(data?.is_shop_license_verified);
  }

  /** True when PAN is verified and at least one of GST / Udyam / Shop License is verified. */
  isFullyVerified(data: BuyerVerificationData): boolean {
    return (
      this.isTruthyFlag(data.pan_verified) &&
      (this.isGstVerified(data) ||
        this.isUdyamVerified(data) ||
        this.isShopLicenseVerified(data) ||
        this.isTruthyFlag(data.route_verified))
    );
  }

  getVerificationProfile(userData: unknown): BuyerVerificationData | null {
    if (!userData || typeof userData !== 'object') {
      return null;
    }
    const profile = (userData as { verification_profile?: BuyerVerificationData })
      .verification_profile;
    return profile ?? null;
  }

  canAddGstDetails(userData: unknown): boolean {
    const profile = this.getVerificationProfile(userData);
    if (!profile) {
      return false;
    }
    return (
      this.isTruthyFlag(profile.pan_verified) &&
      this.isTruthyFlag(profile.route_verified) &&
      !!String(profile.selected_gstin || '').trim()
    );
  }

  canPlaceOrder(profile: any): boolean {
    return canPlaceOrderUtil(profile);
  }

  isFullyVerifiedFromUser(userData: unknown): boolean {
    const profile = this.getVerificationProfile(userData);
    return profile ? this.isFullyVerified(profile) : false;
  }

  /** Returns true only when pan_verified, route_verified, and both user and verification are active. */
  async ensureCanCreateOrder(profile?: any): Promise<OrderCreationGateResult> {
    try {
      let candidate = profile;
      if (
        !candidate ||
        (typeof candidate === 'object' && Object.keys(candidate).length === 0)
      ) {
        const rawUser = localStorage.getItem('userData1');
        if (rawUser) {
          try {
            candidate = JSON.parse(rawUser);
          } catch (_) { }
        }
      }

      if (
        candidate &&
        typeof candidate === 'object' &&
        Object.keys(candidate).length > 0
      ) {
        if (this.canPlaceOrder(candidate)) {
          return { allowed: true, message: '' };
        }
        return {
          allowed: false,
          message: BuyerVerificationService.ORDER_PROFILE_UPDATE_MESSAGE,
        };
      }

      const status = await firstValueFrom(this.getStatus());
      if (this.canPlaceOrder(status)) {
        return { allowed: true, message: '' };
      }
      return {
        allowed: false,
        message: BuyerVerificationService.ORDER_PROFILE_UPDATE_MESSAGE,
      };
    } catch (error) {
      const message =
        error instanceof Error
          ? error.message
          : BuyerVerificationService.ORDER_PROFILE_UPDATE_MESSAGE;
      return { allowed: false, message };
    }
  }

  isGstRoute(data: BuyerVerificationData): boolean {
    return this.getVerificationRoute(data) === 'gst';
  }

  isShopLicenseRoute(data: BuyerVerificationData): boolean {
    return this.getVerificationRoute(data) === 'shop-license';
  }

  getVerificationRoute(
    data: BuyerVerificationData,
  ): 'gst' | 'shop-license' | null {
    const route = data.verification_route ?? data.route ?? null;
    if (route === 'gst') return 'gst';
    if (route === 'shop-license' || route === 'shop_license') {
      return 'shop-license';
    }
    return null;
  }

  private isTruthyFlag(value: unknown): boolean {
    if (value === true || value === 1) {
      return true;
    }
    if (typeof value === 'string') {
      const normalized = value.trim().toLowerCase();
      return normalized === 'true' || normalized === '1';
    }
    return false;
  }

  private extractVerificationData(raw: unknown): BuyerVerificationData {
    if (!raw || typeof raw !== 'object') {
      return { pan_verified: false, route_verified: false };
    }

    const record = raw as Record<string, unknown>;
    const nested = record['verification_profile'];
    if (nested && typeof nested === 'object') {
      return {
        ...(nested as BuyerVerificationData),
        ...(record as unknown as BuyerVerificationData),
      };
    }

    return record as unknown as BuyerVerificationData;
  }

  private normalizeData(data: BuyerVerificationData): BuyerVerificationData {
    const route = this.getVerificationRoute(data);
    return {
      ...data,
      verification_route: route,
      gstins: (data.gstins ?? []).map((item) => this.normalizeGstin(item)),
    };
  }

  isGstinActive(item: GstinItem | null | undefined): boolean {
    return this.getGstinStatus(item).toLowerCase() === 'active';
  }

  isGstDocument(item: GstinItem | null | undefined): boolean {
    const documentType = this.getDocumentType(item);
    const documentNumber = this.getGstinNumber(item).toUpperCase();
    return documentType
      ? documentType === 'gst'
      : !!documentNumber && !documentNumber.startsWith('UDYAM-');
  }

  isUdyamDocument(item: GstinItem | null | undefined): boolean {
    const documentType = this.getDocumentType(item);
    return documentType
      ? documentType === 'udyam'
      : this.getGstinNumber(item).toUpperCase().startsWith('UDYAM-');
  }

  filterGstDocuments(items: GstinItem[] | null | undefined): GstinItem[] {
    return (items ?? []).filter((item) => this.isGstDocument(item));
  }

  filterUdyamDocuments(items: GstinItem[] | null | undefined): GstinItem[] {
    return (items ?? []).filter((item) => this.isUdyamDocument(item));
  }

  getGstinNumber(item: GstinItem | null | undefined): string {
    return (item?.document_number || item?.gstin || '').trim();
  }

  getGstinStatus(item: GstinItem | null | undefined): string {
    return String(item?.status || item?.gstin_status || '').trim();
  }

  private normalizeGstin(item: GstinItem): GstinItem {
    const gstin = this.getGstinNumber(item);
    const gstin_status = this.getGstinStatus(item);
    return {
      ...item,
      gstin,
      document_number: item.document_number || gstin,
      gstin_status,
      status: item.status || gstin_status,
    };
  }

  private getDocumentType(item: GstinItem | null | undefined): string {
    return String(item?.document_type || '')
      .trim()
      .toLowerCase();
  }

  isVerificationSkipped(): boolean {
    return localStorage.getItem(SKIPPED_KEY) === 'true';
  }

  markSkipped(): void {
    localStorage.setItem(SKIPPED_KEY, 'true');
  }

  clearSkipped(): void {
    localStorage.removeItem(SKIPPED_KEY);
  }

  needsVerificationAtCheckout(data: BuyerVerificationData): boolean {
    return !this.isFullyVerified(data);
  }

  openVerificationModal(
    onComplete?: () => void,
    onCancel?: () => void,
  ): void {
    this.pendingPaymentCallback = onComplete ?? null;
    this.pendingCancelCallback = onCancel ?? null;
    this.showModalSubject.next(true);
  }

  closeVerificationModal(): void {
    this.showModalSubject.next(false);
    this.pendingPaymentCallback = null;
    this.pendingCancelCallback = null;
  }

  cancelCheckoutVerification(): void {
    const callback = this.pendingCancelCallback;
    this.closeVerificationModal();
    callback?.();
  }

  completeCheckoutVerification(): void {
    const callback = this.pendingPaymentCallback;
    this.closeVerificationModal();
    callback?.();
  }

  private post(body: Record<string, string>): Observable<BuyerVerificationData> {
    return this.http
      .post<BuyerVerificationResponse>(this.endpoint, body)
      .pipe(
        timeout(20000),
        map((response) => {
          if (!response?.success) {
            const msg =
              response?.data?.msg ||
              (response as any)?.data?.message ||
              (response as any)?.msg ||
              (response as any)?.message ||
              'Verification request failed';
            throw new Error(msg);
          }
          return this.normalizeData(
            this.extractVerificationData(response.data),
          );
        }),
        catchError((error: any) =>
          throwError(() => new Error(this.mapErrorMessage(error))),
        ),
      );
  }

  private mapErrorMessage(error: any): string {
    let errBody = error?.error;
    if (typeof errBody === 'string') {
      try {
        errBody = JSON.parse(errBody);
      } catch {
        // keep string
      }
    }

    const apiMessage =
      errBody?.data?.msg ||
      errBody?.data?.message ||
      errBody?.message ||
      errBody?.msg ||
      error?.data?.msg ||
      error?.data?.message ||
      error?.msg;

    if (typeof apiMessage === 'string' && /existing\s+and\s+valid/i.test(apiMessage)) {
      return 'GSTIN details verified successfully.';
    }

    if (error?.name === 'TimeoutError') {
      return 'Verification service is taking longer than expected. Please retry in a few moments.';
    }

    if (error?.status === 503 || error?.status === 504) {
      return apiMessage || 'Government verification service is temporarily unavailable. Please try again shortly.';
    }

    if (error?.status === 412) {
      return apiMessage || 'Verify PAN before business verification';
    }

    if (apiMessage && typeof apiMessage === 'string') {
      return apiMessage;
    }

    if (error?.message && typeof error.message === 'string' && !error.message.startsWith('Http failure response')) {
      return error.message;
    }

    return 'Something went wrong. Please try again.';
  }
}
